'use client'

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ChevronDownIcon, Text } from '@radix-ui/themes'
import { useQuery, useQueries, useInfiniteQuery } from '@tanstack/react-query'
import { IconArrowLeft, IconArrowRight, IconSearch } from '@tabler/icons-react'
import { chapterService } from '@/services/chapterService'
import { questionService } from '@/services/questionsService'
import { Chapter } from '@/types/chapter'
import {
  CaseStudyListResponse,
  CaseStudyQuestion,
  CaseStudyQuestionListResponse,
  CaseStudyResponse,
  QuestionDetail,
  QuestionListBody,
  QuestionListType,
  QuestionParams,
  QuestionSection,
} from '@/types/question'
import clsx from 'clsx'
import { cn } from '@/lib/utils'
import {
  getMarksPercentage,
  getMarksStyle,
  hoverActiveEffect,
} from '@/utils/style'
import { useDebounce } from '@/hooks/useDebounce'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog'
import Hstack from '@/components/ui/hstack'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import Stack from '@/components/ui/stack'
import SectionDrawer from '..//section-drawer'
// import TablePaginate from '../table-paginate'
import { DataTableDemo } from '../table/checkBox-table'
import CaseStudyTable from './case-study-table'

enum TypeFilterLabel {
  non_case_study = 'All',
  text = 'Standard',
  choice = 'Multiple Choice',
}

const CASE_STUDY_PAGE_SIZE = 10

type Props = {
  title: string
  section?: QuestionSection
  totalQuestion?: number
  duration?: number
  totalMarks?: number
  moduleId: string
  defaultSelectedIds?: string[]
  defaultCaseStudySelected?: Record<number, string[]>
  onSaveSelected?: (payload: {
    section: QuestionSection
    questionIds: string[]
    items: QuestionDetail[]
    caseStudyGroups: Array<{
      caseStudyId: number
      items: CaseStudyQuestion[]
      totalTime: number
      totalMarks: number
      selectedCount: number
    }>
  }) => void
}
export default function SectionHeader({
  title,
  section,
  totalMarks,
  totalQuestion,
  duration,
  moduleId,
  defaultSelectedIds,
  defaultCaseStudySelected,
  onSaveSelected,
}: Props) {
  // Prevent duplicate notifications
  const lastNotifyRef = useRef<string>('')
  const [open, setOpen] = useState<boolean>(false)
  const [selectedMetrics, setSelectedMetrics] = useState({
    selectedCount: 0,
    totalTime: 0,
    totalMarks: 0,
    selectedIds: (defaultSelectedIds || []) as string[],
  })
  // Keep a map of selected non-case-study questions so they still appear when filters change
  const [selectedItemsMap, setSelectedItemsMap] = useState<
    Record<string, QuestionDetail>
  >({})
  const [caseStudySelections, setCaseStudySelections] = useState<
    Record<
      number,
      {
        selectedCount: number
        totalTime: number
        totalMarks: number
        selectedIds: string[]
        selectedItems: CaseStudyQuestion[]
      }
    >
  >(() => {
    const init: Record<
      number,
      {
        selectedCount: number
        totalTime: number
        totalMarks: number
        selectedIds: string[]
        selectedItems: CaseStudyQuestion[]
      }
    > = {}
    const map = defaultCaseStudySelected || {}
    for (const [k, ids] of Object.entries(map)) {
      init[Number(k)] = {
        selectedCount: ids.length,
        totalTime: 0,
        totalMarks: 0,
        selectedIds: ids,
        selectedItems: [],
      }
    }
    return init
  })
  const [showSelectedOnly, setShowSelectedOnly] = useState<boolean>(false)
  const [isReviewMode, setIsReviewMode] = useState<boolean>(false)

  // Reset selections back to defaults (used when opening/closing without saving)
  const resetSelections = useCallback(() => {
    // Allow a new Save after parent/table updates (e.g. removed CS then re-added same selection)
    lastNotifyRef.current = ''
    setSelectedMetrics({
      selectedCount: Array.isArray(defaultSelectedIds)
        ? defaultSelectedIds.length
        : 0,
      totalTime: 0,
      totalMarks: 0,
      selectedIds: (defaultSelectedIds || []) as string[],
    })
    setSelectedItemsMap({})
    setCaseStudySelections(() => {
      const init: Record<
        number,
        {
          selectedCount: number
          totalTime: number
          totalMarks: number
          selectedIds: string[]
          selectedItems: CaseStudyQuestion[]
        }
      > = {}
      const map = defaultCaseStudySelected || {}
      for (const [k, ids] of Object.entries(map)) {
        init[Number(k)] = {
          selectedCount: (ids || []).length,
          totalTime: 0,
          totalMarks: 0,
          selectedIds: (ids || []) as string[],
          selectedItems: [],
        }
      }
      return init
    })
    setShowSelectedOnly(false)
    setIsReviewMode(false)
  }, [defaultSelectedIds, defaultCaseStudySelected])

  // Sync defaults coming from draft edit into local state when props change
  useEffect(() => {
    if (!Array.isArray(defaultSelectedIds) || open) return
    setSelectedMetrics((prev) => {
      const prevStr = JSON.stringify(prev.selectedIds || [])
      const nextStr = JSON.stringify(defaultSelectedIds || [])
      if (prevStr === nextStr) return prev
      return { ...prev, selectedIds: defaultSelectedIds }
    })
  }, [JSON.stringify(defaultSelectedIds), open])

  useEffect(() => {
    if (!defaultCaseStudySelected || open) return
    const init: Record<
      number,
      {
        selectedCount: number
        totalTime: number
        totalMarks: number
        selectedIds: string[]
        selectedItems: CaseStudyQuestion[]
      }
    > = {}
    for (const [k, ids] of Object.entries(defaultCaseStudySelected)) {
      init[Number(k)] = {
        selectedCount: (ids || []).length,
        totalTime: 0,
        totalMarks: 0,
        selectedIds: ids || [],
        selectedItems: [],
      }
    }
    // Only update if it actually changed
    setCaseStudySelections((prev) => {
      const prevStr = JSON.stringify(prev)
      const nextStr = JSON.stringify(init)
      if (prevStr === nextStr) return prev
      return init
    })
  }, [JSON.stringify(defaultCaseStudySelected), open])

  const [params] = useState<QuestionParams>({
    page: 1,
    size: 10,
  })

  const [search, setSearch] = useState<string>('')
  const debouncedSearch = useDebounce(search)
  const [questionListType, setQuestionListType] = useState<QuestionListType>(
    QuestionListType.non_case_study
  )

  const [body, setBody] = useState<QuestionListBody>({
    module_id: Number(moduleId),
    section: section || QuestionSection.A,
    type: questionListType,
    query: search,
  })

  // Create a separate body for chapter filtering
  const [chapterFilterBody, setChapterFilterBody] = useState<QuestionListBody>({
    module_id: Number(moduleId),
    section: section || QuestionSection.A,
    type: questionListType,
    query: search,
  })

  // Update query in request body when search changes (debounced)
  useEffect(() => {
    setBody((prev) => {
      if (prev.query === debouncedSearch) return prev
      return { ...prev, query: debouncedSearch }
    })
    setChapterFilterBody((prev) => {
      if (prev.query === debouncedSearch) return prev
      return { ...prev, query: debouncedSearch }
    })
  }, [debouncedSearch])

  const [isFilterOpen, setIsFilterOpen] = useState<boolean>(false)
  const [selectedChapterIds, setSelectedChapterIds] = useState<string[]>([])

  const handleQuestionListTypeChange = (value: QuestionListType) => {
    // Update current list type
    setQuestionListType(value)

    // Sync type in main body used for fetching questions
    setBody((prev) => ({
      ...prev,
      type: value,
    }))

    // Sync type in chapter filter body so filtered requests respect selected type
    setChapterFilterBody((prev) => ({
      ...prev,
      type: value,
    }))
  }

  // Update chapter filter body when selectedChapterIds change
  useEffect(() => {
    setChapterFilterBody((prev) => ({
      ...prev,
      module_id: Number(moduleId),
      section: title as QuestionSection,
      query: debouncedSearch,
      type: questionListType,
      chapter_ids:
        selectedChapterIds.length > 0 ? selectedChapterIds : undefined,
    }))
  }, [selectedChapterIds, moduleId, title, debouncedSearch, questionListType])
  const { data: chaptersResp, isFetching: isChaptersLoading } = useQuery({
    queryKey: ['chapters', moduleId, isFilterOpen],
    queryFn: () =>
      chapterService.getChapters({
        module_id: String(moduleId),
        search: '',
        page: 1,
        limit: 100,
      }),
    enabled: isFilterOpen && !!moduleId,
  })
  const chapters: Chapter[] = useMemo(
    () => (chaptersResp?.data || []) as unknown as Chapter[],
    [chaptersResp]
  )

  useEffect(() => {
    setBody((prev) => ({
      ...prev,
      module_id: Number(moduleId),
      section: title as QuestionSection,
      type: questionListType,
      // do not override query here; search is managed by debouncedSearch effect
    }))
  }, [title, moduleId, questionListType])

  const {
    data: qPages,
    fetchNextPage,
    hasNextPage,
    // isFetching: isQuestionsLoading,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ['questions-infinite', body, params.size],
    initialPageParam: 1,
    queryFn: ({ pageParam }) =>
      questionService.getQuestions(
        { page: pageParam as number, size: params.size },
        body
      ),
    getNextPageParam: (lastPage) => {
      const current = lastPage?.meta?.current_page || 1
      const total = lastPage?.meta?.total_pages || 1
      return current < total ? current + 1 : undefined
    },
  })

  // Separate query for chapter-filtered questions
  const {
    data: chapterFilteredPages,
    fetchNextPage: fetchNextChapterPage,
    hasNextPage: hasNextChapterPage,
    // isFetching: isChapterFilterLoading,
    isFetchingNextPage: isFetchingNextChapterPage,
  } = useInfiniteQuery({
    queryKey: ['questions-chapter-filter', chapterFilterBody, params.size],
    initialPageParam: 1,
    queryFn: ({ pageParam }) =>
      questionService.getQuestions(
        { page: pageParam as number, size: params.size },
        chapterFilterBody
      ),
    getNextPageParam: (lastPage) => {
      const current = lastPage?.meta?.current_page || 1
      const total = lastPage?.meta?.total_pages || 1
      return current < total ? current + 1 : undefined
    },
    enabled: selectedChapterIds.length > 0, // Only fetch when chapter filter is active
  })

  const allQuestions = useMemo(() => {
    const pages = qPages?.pages || []
    const flat = pages.flatMap((p: any) => (p?.data || []) as QuestionDetail[])
    return flat
  }, [qPages])

  const chapterFilteredQuestions = useMemo(() => {
    const pages = chapterFilteredPages?.pages || []
    const flat = pages.flatMap((p: any) => (p?.data || []) as QuestionDetail[])
    return flat
  }, [chapterFilteredPages])

  // Combine questions based on filter state
  const combinedQuestions = useMemo(() => {
    if (selectedChapterIds.length > 0) {
      // When chapter filter is active, use chapter-filtered questions
      return chapterFilteredQuestions
    }
    // Otherwise use all questions
    return allQuestions
  }, [allQuestions, chapterFilteredQuestions, selectedChapterIds.length])

  // Sync selectedItemsMap with combinedQuestions to ensure selected items are always available
  useEffect(() => {
    setSelectedItemsMap((prevMap) => {
      const nextMap = { ...prevMap }
      let hasChanges = false
      // Add or update any questions from combinedQuestions that are currently selected
      for (const q of combinedQuestions || []) {
        const id = String(q.question_id)
        if (selectedMetrics.selectedIds.includes(id)) {
          // Always update to get the latest data from API
          if (!nextMap[id]) {
            nextMap[id] = q
            hasChanges = true
          }
        }
      }
      return hasChanges ? nextMap : prevMap
    })
  }, [combinedQuestions, selectedMetrics.selectedIds])

  // Merge fetched questions with previously selected ones to keep them visible even when filtered out
  const mergedNonCaseQuestions: QuestionDetail[] = useMemo(() => {
    // const byId: Record<string, QuestionDetail> = {}

    // // Add all fetched questions (from current filter or chapter filter)
    // for (const q of combinedQuestions as QuestionDetail[]) {
    //   byId[String(q.question_id)] = q
    // }

    // Start with the original API order from combinedQuestions
    const result: QuestionDetail[] = [
      ...(combinedQuestions as QuestionDetail[]),
    ]
    const existingIds = new Set(result.map((q) => String(q.question_id)))

    // Add previously selected questions that might not be in current filter results
    for (const [id, q] of Object.entries(selectedItemsMap)) {
      // if (!byId[id]) {
      //   byId[id] = q
      if (!existingIds.has(id)) {
        result.push(q)
        existingIds.add(id)
      }
    }

    // Also ensure all currently selected questions are included
    for (const selectedId of selectedMetrics.selectedIds || []) {
      // if (!byId[selectedId]) {
      //   // Find the question in selectedItemsMap or create a placeholder
      if (!existingIds.has(selectedId)) {
        // Find the question in selectedItemsMap
        const existingItem = selectedItemsMap[selectedId]
        if (existingItem) {
          //       byId[selectedId] = existingItem
          //     }
          //   }
          // }

          // // When chapter filter is active, we already have filtered questions from API
          // // So we just return the merged questions without additional filtering
          // if (selectedChapterIds.length > 0) {
          //   return Object.values(byId)
          // }

          // // When no chapter filter, show all questions
          // return Object.values(byId)
          result.push(existingItem)
          existingIds.add(selectedId)
        }
      }
    }

    return result
  }, [
    combinedQuestions,
    selectedItemsMap,
    selectedMetrics.selectedIds,
    // selectedChapterIds,
  ])

  // Fetch case studies at parent level (avoid nested updates in child)
  const {
    data: caseStudiesData,
    fetchNextPage: fetchNextCaseStudyPage,
    hasNextPage: hasNextCaseStudyPage,
    isFetchingNextPage: isFetchingNextCaseStudyPage,
    // isFetching: isCaseStudyQuestionLoading
  } = useInfiniteQuery({
    queryKey: ['case-studies-infinite', moduleId, title],
    initialPageParam: 1,
    queryFn: ({ pageParam }) =>
      questionService.getCaseStudies({
        module_id: Number(moduleId),
        section: title as QuestionSection,
        search: '',
        page: pageParam as number,
        size: CASE_STUDY_PAGE_SIZE,
      }),
    getNextPageParam: (lastPage) => {
      const current = lastPage?.meta?.current_page || 1
      const total = lastPage?.meta?.total_pages || 1
      return current < total ? current + 1 : undefined
    },
  })

  const caseStudies: CaseStudyResponse[] = useMemo(() => {
    const pages = caseStudiesData?.pages || []
    return pages.flatMap((p) => {
      const payload = (p as unknown as CaseStudyListResponse | undefined)?.data
      if (!payload) return []
      return Array.isArray(payload) ? payload : [payload]
    })
  }, [caseStudiesData])

  const questionsByCaseStudyResponses = useQueries({
    queries: caseStudies.map((cs) => ({
      queryKey: ['case-study-questions', cs.case_study_id],
      queryFn: () => questionService.getQuestionByCaseStudy(cs.case_study_id),
      enabled: !!cs.case_study_id,
    })),
  })

  const questionsByCaseStudy = useMemo(() => {
    const map: Record<number, CaseStudyQuestion[]> = {}
    caseStudies.forEach((cs, idx) => {
      const resp = questionsByCaseStudyResponses[idx]?.data as unknown as
        | CaseStudyQuestionListResponse
        | undefined
      const list = resp?.questions ?? resp?.question ?? []
      map[cs.case_study_id] = list || []
    })
    return map
  }, [caseStudies, questionsByCaseStudyResponses])

  const handleCaseStudySelectionChange = useCallback(
    (payload: {
      caseStudyId: number
      selectedIds: string[]
      totalTime: number
      totalMarks: number
      selectedCount: number
      selectedItems: CaseStudyQuestion[]
    }) => {
      setCaseStudySelections((prev) => {
        const next = { ...prev }
        if (!payload.selectedCount) {
          delete next[payload.caseStudyId]
          return next
        }
        next[payload.caseStudyId] = {
          selectedCount: payload.selectedCount,
          totalTime: payload.totalTime,
          totalMarks: payload.totalMarks,
          selectedIds: payload.selectedIds,
          selectedItems: payload.selectedItems,
        }
        return next
      })
    },
    []
  )

  const combinedSelectedIds = useMemo(() => {
    const caseStudyIds = Object.values(caseStudySelections).flatMap(
      (v) => v.selectedIds || []
    )
    return [...(selectedMetrics.selectedIds || []), ...caseStudyIds]
  }, [selectedMetrics.selectedIds, caseStudySelections])
  const combinedSelectedCount = useMemo(() => {
    const csCount = Object.values(caseStudySelections).reduce(
      (sum, v) => sum + (v.selectedCount || 0),
      0
    )
    return (selectedMetrics.selectedCount || 0) + csCount
  }, [selectedMetrics.selectedCount, caseStudySelections])
  const combinedTotalTime = useMemo(() => {
    const csTime = Object.values(caseStudySelections).reduce(
      (sum, v) => sum + (v.totalTime || 0),
      0
    )
    return (selectedMetrics.totalTime || 0) + csTime
  }, [selectedMetrics.totalTime, caseStudySelections])
  const combinedTotalMarks = useMemo(() => {
    const csMarks = Object.values(caseStudySelections).reduce(
      (sum, v) => sum + (v.totalMarks || 0),
      0
    )
    return (selectedMetrics.totalMarks || 0) + csMarks
  }, [selectedMetrics.totalMarks, caseStudySelections])

  return (
    <>
      <Hstack justify={'between'} align={'center'}>
        <Stack gap={'0'}>
          <Text className='text-[24px] font-medium text-[#002652]'>
            Section {title}
          </Text>
          <Hstack gap={'32px'}>
            <Text className='text-[16px] font-normal text-[#1D2939]'>
              Total Questions:{' '}
              <span className='text-[16px] font-medium text-[#101828]'>
                {totalQuestion}
              </span>
            </Text>
            <Text className='text-[16px] font-normal text-[#1D2939]'>
              Duration:{' '}
              <span className='text-[16px] font-medium text-[#101828]'>
                {duration} minutes
              </span>
            </Text>
            <Text className='text-[16px] font-normal text-[#1D2939]'>
              Total Marks Available:{' '}
              <span className='text-[16px] font-medium text-[#101828]'>
                {totalMarks}
              </span>
            </Text>
          </Hstack>
        </Stack>
        <Button
          className='bg-[#0057B8] text-white hover:bg-[#0057B8]'
          onClick={() => {
            // Clear filter when opening drawer to show all questions
            setBody((prev) => ({
              ...prev,
              chapter_ids: undefined, // Remove chapter_ids filter from API call
            }))
            setSelectedChapterIds([])
            setSearch('')
            // Reset temporary selections back to saved defaults
            resetSelections()
            setOpen(true)
          }}
        >
          Add Questions
        </Button>
      </Hstack>

      <SectionDrawer
        open={open}
        onClose={() => {
          // Clear filter when closing drawer
          setBody((prev) => ({
            ...prev,
            chapter_ids: undefined, // Remove chapter_ids filter from API call
            type: QuestionListType.non_case_study,
          }))
          setChapterFilterBody((prev) => ({
            ...prev,
            type: QuestionListType.non_case_study,
          }))
          setQuestionListType(QuestionListType.non_case_study)
          setSelectedChapterIds([])
          setSearch('')
          setIsFilterOpen(false)
          // Revert selections back to saved defaults when closing without saving
          resetSelections()
          setOpen(false)
        }}
        title={`Select Question for Section ${title}`}
        className='flex h-full max-h-screen w-screen flex-col'
      >
        {(combinedQuestions?.length ?? 0) === 0 &&
        caseStudies.length === 0 &&
        // (isCaseStudyQuestionLoading ||
        //   isQuestionsLoading ||
        //   isChapterFilterLoading) ? (
        //   <Text mt={'24px'}>Loading...</Text>
        // ) :
        (combinedQuestions?.length ?? 0) === 0 &&
        caseStudies.length === 0 &&
        questionListType === QuestionListType.non_case_study &&
        (debouncedSearch?.trim?.() || '').length === 0 ? (
          <Stack justify={'center'} mt={'24px'} gap={'8px'}>
            <Text>There are no questions in this Section.</Text>
            <Text>Please contact Admin.</Text>
          </Stack>
        ) : (
          <div className='flex h-full min-h-0 flex-1 flex-col pb-4'>
            <div
              className='min-h-0 flex-1 overflow-y-auto px-0 py-0'
              onScroll={(e) => {
                const el = e.currentTarget
                // Use appropriate fetch function based on filter state
                const shouldFetch =
                  selectedChapterIds.length > 0
                    ? hasNextChapterPage
                    : hasNextPage
                const fetchFn =
                  selectedChapterIds.length > 0
                    ? fetchNextChapterPage
                    : fetchNextPage

                if (!shouldFetch) return
                // Auto-load when scrolled near bottom
                if (el.scrollTop + el.clientHeight >= el.scrollHeight - 200) {
                  fetchFn()
                }
              }}
            >
              <Stack gap={'12px'}>
                <Hstack
                  justify={'start'}
                  className='max-w-[300px] rounded-md border-[1px] border-gray-400 bg-white px-2 py-1.5'
                >
                  <IconSearch color='#475467' size={'20px'} />
                  <input
                    placeholder='Search'
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className='w-full outline-none'
                  />
                </Hstack>
                <Hstack justify={'between'} align={'center'}>
                  {!isReviewMode ? (
                    <Button
                      className={`h-8 rounded-md px-3 py-1 text-[#0069E0] ${hoverActiveEffect} bg-transparent hover:bg-transparent`}
                      aria-label={'Show selected questions'}
                    >
                      <Hstack gap={'8px'} align={'center'}>
                        <Text className={clsx('text-[16px] font-normal')}>
                          {combinedSelectedCount} selected
                        </Text>
                      </Hstack>
                    </Button>
                  ) : (
                    <Text></Text>
                  )}

                  <Hstack gap={'32px'}>
                    <Text className='text-[16px] font-extralight text-[#1D2939]'>
                      Duration: <span>{combinedTotalTime} minutes</span>
                    </Text>
                    <Text className='text-[16px] font-extralight text-[#1D2939]'>
                      Total Marks Available:{' '}
                      <span className='text-[16px] font-normal text-[#101828]'>
                        {combinedTotalMarks}
                      </span>
                    </Text>
                  </Hstack>
                </Hstack>
                <Hstack justify={'between'}>
                  <Text className='text-[16px] font-extralight text-[#1D2939]'>
                    Non-Case Study Questions
                  </Text>
                  <Hstack gap={'12px'}>
                    <Button
                      className={`border border-[#EAECF0] bg-white text-black ${hoverActiveEffect} hover:bg-white`}
                      onClick={() => {
                        setSelectedChapterIds(selectedChapterIds)
                        setIsFilterOpen(true)
                      }}
                    >
                      {/* <IconFilter size={'20px'} /> */}
                      {selectedChapterIds.length > 0
                        ? `Chapter: ${selectedChapterIds.length} selected`
                        : 'Chapter: All'}
                      <ChevronDownIcon className='size-2 opacity-50' />
                    </Button>
                    <Select
                      value={questionListType}
                      onValueChange={(value) =>
                        handleQuestionListTypeChange(value as QuestionListType)
                      }
                    >
                      <SelectTrigger className='h-9 w-full'>
                        Type: <SelectValue placeholder='All' />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={QuestionListType.non_case_study}>
                          {TypeFilterLabel.non_case_study}
                        </SelectItem>
                        <SelectItem value={QuestionListType.text}>
                          {TypeFilterLabel.text}
                        </SelectItem>
                        <SelectItem value={QuestionListType.choice}>
                          {TypeFilterLabel.choice}
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </Hstack>
                </Hstack>
                <Stack
                  gap={'16px'}
                  pb={'20px'}
                  className='max-h-[800px] rounded-[16px]'
                >
                  <DataTableDemo
                    questions={
                      (showSelectedOnly
                        ? (
                            (mergedNonCaseQuestions as
                              | QuestionDetail[]
                              | undefined) || []
                          ).filter((q) =>
                            selectedMetrics.selectedIds.includes(
                              String((q as QuestionDetail).question_id)
                            )
                          )
                        : (mergedNonCaseQuestions as QuestionDetail[])) as QuestionDetail[]
                    }
                    onSelectionChange={(metrics) => {
                      setSelectedMetrics(() => {
                        // If metrics has no selection, clear everything
                        if (
                          !metrics.selectedIds ||
                          metrics.selectedIds.length === 0
                        ) {
                          return {
                            selectedCount: 0,
                            totalTime: 0,
                            totalMarks: 0,
                            selectedIds: [],
                          }
                        }

                        // Otherwise, use the metrics directly
                        return {
                          selectedCount: metrics.selectedCount || 0,
                          totalTime: metrics.totalTime || 0,
                          totalMarks: metrics.totalMarks || 0,
                          selectedIds: metrics.selectedIds || [],
                        }
                      })

                      // Update selection map with current selection
                      setSelectedItemsMap(() => {
                        const nextMap: Record<string, QuestionDetail> = {}

                        // Only keep items that are currently selected
                        for (const item of metrics.selectedItems || []) {
                          nextMap[String(item.question_id)] = item
                        }

                        return nextMap
                      })
                    }}
                    enableSelection={!isReviewMode}
                    selectedIds={selectedMetrics.selectedIds}
                    onFetchNextPage={() => {
                      if (selectedChapterIds.length > 0) {
                        fetchNextChapterPage()
                      } else {
                        fetchNextPage()
                      }
                    }}
                    hasNextPage={
                      selectedChapterIds.length > 0
                        ? hasNextChapterPage
                        : hasNextPage
                    }
                  />
                  {/* <TablePaginate
                    className='justify-end pr-[24px]'
                    page={questions?.meta?.current_page || 1}
                    totalPages={questions?.meta?.total_pages || 1}
                    onPageChange={(nextPage) => {
                      if (showSelectedOnly) return
                      setParams((prev) => ({
                        ...prev,
                        page: nextPage,
                      }))
                    }}
                  /> */}
                  <CaseStudyTable
                    caseStudies={
                      isReviewMode
                        ? caseStudies.filter(
                            (cs) =>
                              (caseStudySelections[cs.case_study_id]
                                ?.selectedIds?.length || 0) > 0
                          )
                        : caseStudies
                    }
                    questionsByCaseStudy={questionsByCaseStudy}
                    onSelectionChange={handleCaseStudySelectionChange}
                    reviewModeSelected={
                      isReviewMode
                        ? Object.fromEntries(
                            Object.entries(caseStudySelections).map(
                              ([k, v]) => [Number(k), v.selectedIds]
                            )
                          )
                        : undefined
                    }
                    initialSelected={defaultCaseStudySelected}
                    onFetchNextPage={fetchNextCaseStudyPage}
                    hasNextPage={hasNextCaseStudyPage}
                    isFetchingNextPage={isFetchingNextCaseStudyPage}
                  />
                  {(isFetchingNextPage || isFetchingNextChapterPage) && (
                    <Text className='text-center text-[12px] text-[#475467]'>
                      Loading more...
                    </Text>
                  )}
                </Stack>
              </Stack>
            </div>
            <Hstack justify={'between'} pt={'20px'}>
              {isReviewMode ? (
                <Hstack justify={'between'} align={'center'}>
                  <Button
                    className={`h-8 rounded-md px-3 py-1 text-[#0069E0] ${hoverActiveEffect} bg-transparent hover:bg-transparent`}
                    aria-label={'Show all questions'}
                    onClick={() => {
                      setShowSelectedOnly(false)
                      setIsReviewMode(false)
                    }}
                  >
                    <Hstack gap={'8px'} align={'center'}>
                      <IconArrowLeft color='#0069E0' size={'20px'} />
                      <Text className={clsx('text-[16px] font-normal')}>
                        All Questions
                      </Text>
                    </Hstack>
                  </Button>
                </Hstack>
              ) : (
                <Text></Text>
              )}
              <Hstack justify={'end'} gap={'8px'} pt={'20px'}>
                <Button
                  className={`border border-[#EAECF0] bg-white text-[#475467] ${hoverActiveEffect} hover:bg-transparent`}
                  onClick={() => {
                    if (!isReviewMode) {
                      // Clear filter when canceling
                      setBody((prev) => ({
                        ...prev,
                        chapter_ids: undefined, // Remove chapter_ids filter from API call
                      }))
                      setSelectedChapterIds([])
                      setSearch('')
                      // Revert selections back to saved defaults when canceling without saving
                      resetSelections()
                      setOpen(false)
                      return
                    } else {
                      setShowSelectedOnly(false)
                    }
                    setIsReviewMode(false)
                  }}
                >
                  Cancel
                </Button>
                <Button
                  className={`border border-[#EAECF0] bg-[#0057B8] text-white ${hoverActiveEffect} hover:bg-[#7aa4f3fc]`}
                  onClick={() => {
                    if (!isReviewMode) {
                      setShowSelectedOnly(true)
                      setIsReviewMode(true)
                      return
                    }
                    // Get all selected items from both current questions and previously selected items
                    const allSelectedItems: QuestionDetail[] = []

                    // Add items from current questions (use combinedQuestions)
                    for (const q of combinedQuestions || []) {
                      if (
                        selectedMetrics.selectedIds.includes(
                          String(q.question_id)
                        )
                      ) {
                        allSelectedItems.push(q)
                      }
                    }

                    // Add items from selectedItemsMap that might not be in current questions
                    for (const selectedId of selectedMetrics.selectedIds ||
                      []) {
                      const existingItem = selectedItemsMap[selectedId]
                      if (
                        existingItem &&
                        !allSelectedItems.some(
                          (item) => String(item.question_id) === selectedId
                        )
                      ) {
                        allSelectedItems.push(existingItem)
                      }
                    }

                    const filteredItems = allSelectedItems
                    // Create unique key to prevent duplicate notifications
                    const notifyKey = JSON.stringify({
                      questionIds: combinedSelectedIds.sort(),
                      items: filteredItems.map((i) => i.question_id).sort(),
                      section: section || title,
                    })

                    if (lastNotifyRef.current !== notifyKey) {
                      lastNotifyRef.current = notifyKey
                      onSaveSelected?.({
                        section: section || (title as QuestionSection),
                        questionIds: combinedSelectedIds,
                        items: filteredItems,
                        caseStudyGroups: Object.entries(
                          caseStudySelections
                        ).map(([caseStudyId, v]) => ({
                          caseStudyId: Number(caseStudyId),
                          items: v.selectedItems || [],
                          totalTime: v.totalTime || 0,
                          totalMarks: v.totalMarks || 0,
                          selectedCount: v.selectedCount || 0,
                        })),
                      })
                    }
                    // Clear filter after successful save
                    setBody((prev) => ({
                      ...prev,
                      chapter_ids: undefined, // Remove chapter_ids filter from API call
                      type: QuestionListType.non_case_study,
                    }))
                    setChapterFilterBody((prev) => ({
                      ...prev,
                      type: QuestionListType.non_case_study,
                    }))
                    setQuestionListType(QuestionListType.non_case_study)
                    setSelectedChapterIds([])
                    setSearch('')
                    setIsFilterOpen(false)
                    setShowSelectedOnly(false)
                    setOpen(false)
                    setIsReviewMode(false)
                  }}
                >
                  {isReviewMode ? 'Save' : 'Next'}
                </Button>
              </Hstack>
            </Hstack>
          </div>
        )}
      </SectionDrawer>

      <Dialog open={isFilterOpen} onOpenChange={setIsFilterOpen}>
        <DialogTitle />
        <DialogContent
          className='sm:max-w-2xl md:max-w-5xl'
          showCloseButton={false}
        >
          <div className='flex items-center justify-between'>
            <Text className='text-[20px] font-medium text-[#002652]'>
              Filter by Chapter
            </Text>
            <button
              className='flex items-center gap-2 text-[#0069E0]'
              onClick={() => {
                // Don't send chapter_ids to API, we filter client-side
                setIsFilterOpen(false)
              }}
              aria-label='Apply chapter filter'
            >
              <span className='text-[14px]'>Filter</span>
              <IconArrowRight size={'18px'} color={'#0069E0'} />
            </button>
          </div>
          <div>
            <span className='text-[14px] text-[#0069E0]'>
              {selectedChapterIds.length} selected
            </span>
          </div>
          <div className='rounded-md border bg-white'>
            <div className='grid grid-cols-12 gap-0 border-b bg-[#F2F4F7] py-2'>
              <div className='col-span-1 flex items-center justify-center pl-4'>
                <Checkbox
                  aria-label='Select all chapters'
                  checked={
                    chapters.length > 0 &&
                    (selectedChapterIds.length === chapters.length ||
                      (selectedChapterIds.length > 0 && 'indeterminate'))
                  }
                  onCheckedChange={(value) => {
                    if (!value) {
                      setSelectedChapterIds([])
                      return
                    }
                    setSelectedChapterIds(
                      chapters.map((c) => String(c.chapter_id))
                    )
                  }}
                />
              </div>
              <div className='col-span-5 px-4 pl-4 text-[14px] font-medium text-[#002652]'>
                Chapter
              </div>
              <div className='col-span-2 px-4 text-center text-[14px] font-medium text-[#002652]'>
                Total Question
              </div>
              <div className='col-span-2 px-4 text-center text-[14px] font-medium text-[#002652]'>
                Average AI Mark
              </div>
              <div className='col-span-2 px-4 text-center text-[14px] font-medium text-[#002652]'>
                Average Tutor Mark
              </div>
            </div>
            <div className='max-h-[360px] overflow-auto'>
              {isChaptersLoading ? (
                <div className='p-4 text-center text-sm'>
                  Loading chapters...
                </div>
              ) : chapters.length === 0 ? (
                <div className='p-4 text-center text-sm'>No chapters.</div>
              ) : (
                chapters.map((ch) => {
                  const id = String(ch.chapter_id)
                  const isChecked = selectedChapterIds.includes(id)
                  return (
                    <div
                      key={id}
                      className='grid grid-cols-12 items-center gap-0 border-b'
                    >
                      <div className='col-span-1 flex items-center justify-center py-2 pl-4'>
                        <Checkbox
                          aria-label={`select ${ch.chapter_name}`}
                          checked={isChecked}
                          onCheckedChange={(value) => {
                            setSelectedChapterIds((prev) => {
                              const next = new Set(prev.map(String))
                              if (!value) next.delete(id)
                              else next.add(id)
                              return Array.from(next)
                            })
                          }}
                        />
                      </div>
                      <div className='col-span-5 px-4 py-2 text-sm text-[#1D2939]'>
                        {ch.chapter_name}
                      </div>
                      <div className='col-span-2 px-4 py-2 text-center text-sm'>
                        {ch.total_questions}
                      </div>
                      <div
                        className={cn(
                          'col-span-2 flex h-full items-center justify-center py-2 text-center text-sm',
                          getMarksStyle(ch.ai_marking)
                        )}
                      >
                        {getMarksPercentage(ch.ai_marking)}
                      </div>
                      <div
                        className={cn(
                          'col-span-2 flex h-full items-center justify-center py-2 text-center text-sm',
                          getMarksStyle(ch.tutor_marking)
                        )}
                      >
                        {getMarksPercentage(ch.tutor_marking)}
                      </div>
                    </div>
                  )
                })
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}
